import { useQueryClient } from '@tanstack/react-query';
import { Stack, toast } from 'component-library';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CSV_REPORT_TYPES, REPORT_TYPES } from '@/api/rest/resources/types/reports';
import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { Report } from '@/components/widgets/o/ReportCard/ReportCard';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useOrganizationReports } from '@/hooks/useOrganizationReports';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useIsLimitedAccessUser } from '@/pages/shared/hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '@/pages/shared/hooks/useIsOrganizationMember';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationAutomatedReports } from '@/pages/shared/hooks/useReports';

import { ComingSoon } from '../components/ComingSoon';
import { useMenuItems } from '../hooks/useMenuItems';
import { DataExportSection } from './components/DataExportSection';
import { DownloadableInSituFiles } from './components/DownloadableInSituFiles';
import { ReportsList } from './components/ReportsList';

export const ReportsPage = () => {
  const { t } = useTranslation();
  const menuItems = useMenuItems();
  const queryClient = useQueryClient();
  const orgId = useOrganizationId();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const hideReports = isLimitedAccessUser || !isOrganizationMember;

  const widgetSlotsConfiguration = useActiveWidgetSlotConfiguration(
    DefaultWidgetNames.default_template_reports,
    WidgetType.t_page,
  );

  const { data: reportsData } = useOrganizationReports();
  const { data: inProgressData } = useOrganizationAutomatedReports();
  const inProgressReports = useMemo(() => inProgressData?.results || [], [inProgressData]);

  // Track report completion by ID because each "Generate" click creates a new record,
  // even for the same report type. Multiple reports of the same type can be in-progress simultaneously.
  const prevInProgressIdsRef = useRef<Set<string>>(new Set());
  const prevInProgressDataRef = useRef<Map<string, (typeof inProgressReports)[0]>>(new Map());
  // Defensive guard to prevent duplicate toasts (e.g., React strict mode, timing edge cases)
  const completedReportIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const currentInProgressIds = new Set(inProgressReports.map((report) => report.id));
    const prevIds = prevInProgressIdsRef.current;
    // Reports that were in-progress but are no longer returned by the API have completed
    const completedIds = [...prevIds].filter((id) => !currentInProgressIds.has(id));

    if (completedIds.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['organization', 'reports', { organization_id: orgId }] });

      completedIds.forEach((completedId) => {
        if (completedReportIdsRef.current.has(completedId)) {
          return;
        }

        const completedReport = prevInProgressDataRef.current.get(completedId);
        if (!completedReport) {
          return;
        }

        const reportType = completedReport.report_type as (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];
        const csvReportTypes: string[] = Object.values(CSV_REPORT_TYPES);

        if (csvReportTypes.includes(reportType)) {
          completedReportIdsRef.current.add(completedId);
          return;
        }

        const reportLabel = t(`reports.reportTypes.${reportType}`);
        toast({
          title: t('reports.toasts.reportReady', { reportType: reportLabel }),
          type: 'success',
        });
        completedReportIdsRef.current.add(completedId);
      });
    }

    prevInProgressIdsRef.current = currentInProgressIds;
    prevInProgressDataRef.current = new Map(inProgressReports.map((report) => [report.id, report]));
  }, [inProgressReports, queryClient, orgId, t]);

  const handleDownload = (report: Report) => {
    window.open(report.file_download_url, '_blank');
  };

  return (
    <SignedInPageLayout
      items={menuItems}
      navBarProps={{
        title: t('navbar.reports'),
        showFilters: true,
      }}
    >
      <Stack spacing={10}>
        {!hideReports ? (
          <>
            <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
            <ReportsList reports={reportsData.results} onDownload={handleDownload} totalReports={reportsData.count} />
            <DownloadableInSituFiles />
            <DataExportSection />
          </>
        ) : (
          <ComingSoon />
        )}
      </Stack>
    </SignedInPageLayout>
  );
};
